import React, { useState, useEffect } from 'react';

const CONFERENCE_API = process.env.REACT_APP_CONFERENCE_API_URL || 'http://localhost:8080/conference';
const REGISTRATION_API_URL = process.env.REACT_APP_REGISTRATION_API_URL || 'http://localhost:8085';

const Analytics = () => {
  const [conferences, setConferences] = useState([]);
  const [selectedConferenceId, setSelectedConferenceId] = useState('');
  const [selectedConference, setSelectedConference] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({
    paid: 0,
    pending: 0,
    failed: 0,
    attended: 0,
    total: 0,
    onlinePayments: 0,
    offlinePayments: 0
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchActiveConferences();
  }, []);

  useEffect(() => {
    if (selectedConferenceId) {
      fetchAnalyticsData();
    }
  }, [selectedConferenceId]);

  const fetchActiveConferences = async () => {
    try {
      console.log('Fetching active conferences...');
      const response = await fetch(`${CONFERENCE_API}/active`);
      console.log('Response status:', response.status);
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched conferences:', data);
        setConferences(data);
      } else {
        console.error('Failed to fetch conferences:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching conferences:', error);
    }
  };

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      console.log('Fetching analytics for conference:', selectedConference);
      
      // Use conference name which matches what's stored in registration records
      const conferenceIdentifier = selectedConference?.name || '';
      console.log('Using conference name:', conferenceIdentifier);
      
      // Fetch with large page size to get all registrations for analytics
      const response = await fetch(`${REGISTRATION_API_URL}/registration/conference/${encodeURIComponent(conferenceIdentifier)}?page=0&size=10000`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched registration data:', data);
        
        // Handle paginated response
        const registrations = Array.isArray(data) ? data : (data.content || []);
        console.log('Registrations array:', registrations);
        console.log('Total registrations found:', registrations.length);
        
        // Log first registration to see structure
        if (registrations.length > 0) {
          console.log('Sample registration data:', registrations[0]);
          console.log('Payment field:', registrations[0].payment);
          console.log('PaymentStatus field:', registrations[0].paymentStatus);
          console.log('Attended field:', registrations[0].attended);
        }
        
        // Calculate paid, pending, and attended counts
        const paidList = registrations.filter(r => {
          // Check payment array
          if (Array.isArray(r.payment) && r.payment.length > 0) {
            return r.payment.some(p => p.status === 'SUCCESS' || p.status === 'PAID');
          }
          // Check single payment object
          if (r.payment && r.payment.status) {
            return r.payment.status === 'SUCCESS' || r.payment.status === 'PAID';
          }
          // Check paymentStatus field
          return r.paymentStatus === 'PAID' || r.paymentStatus === 'SUCCESS';
        });
        
        const failedList = registrations.filter(r => {
          // Check payment array
          if (Array.isArray(r.payment) && r.payment.length > 0) {
            return r.payment.some(p => p.status === 'FAILED' || p.status === 'Failed');
          }
          // Check single payment object
          if (r.payment && r.payment.status) {
            return r.payment.status === 'FAILED' || r.payment.status === 'Failed';
          }
          // Check paymentStatus field
          return r.paymentStatus === 'FAILED' || r.paymentStatus === 'Failed';
        });
        
        const pendingList = registrations.filter(r => {
          // Check payment array
          if (Array.isArray(r.payment) && r.payment.length > 0) {
            const hasFailed = r.payment.some(p => p.status === 'FAILED' || p.status === 'Failed');
            const hasSuccess = r.payment.some(p => p.status === 'SUCCESS' || p.status === 'PAID');
            return !hasFailed && !hasSuccess;
          }
          // Check single payment object
          if (r.payment && r.payment.status) {
            return r.payment.status !== 'SUCCESS' && r.payment.status !== 'PAID' && r.payment.status !== 'FAILED' && r.payment.status !== 'Failed';
          }
          // Check paymentStatus field or default to pending
          return !r.paymentStatus || r.paymentStatus === 'PENDING' || r.paymentStatus === 'UNPAID';
        });
        
        const attendedList = registrations.filter(r => r.attended === true);
        
        // Calculate online and offline payment counts - one per registration ID
        // Only count registrations with successful payments, check payment type
        let onlineCount = 0;
        let offlineCount = 0;
        
        paidList.forEach(r => {
          // Check payment array - find first successful payment to determine type
          if (Array.isArray(r.payment) && r.payment.length > 0) {
            const successfulPayment = r.payment.find(p => p.status === 'SUCCESS' || p.status === 'PAID');
            if (successfulPayment) {
              if (successfulPayment.paymentType === 'ONLINE') {
                onlineCount++;
              } else if (successfulPayment.paymentType === 'OFFLINE') {
                offlineCount++;
              }
            }
          }
          // Check single payment object
          else if (r.payment && r.payment.status && (r.payment.status === 'SUCCESS' || r.payment.status === 'PAID')) {
            if (r.payment.paymentType === 'ONLINE') {
              onlineCount++;
            } else if (r.payment.paymentType === 'OFFLINE') {
              offlineCount++;
            }
          }
        });
        
        console.log('Paid registrations:', paidList.length);
        console.log('Online payments:', onlineCount);
        console.log('Offline payments:', offlineCount);
        console.log('Failed registrations:', failedList.length);
        console.log('Pending registrations:', pendingList.length);
        console.log('Attended registrations:', attendedList.length);
        
        setAnalyticsData({
          paid: paidList.length,
          pending: pendingList.length,
          failed: failedList.length,
          attended: attendedList.length,
          total: registrations.length,
          onlinePayments: onlineCount,
          offlinePayments: offlineCount
        });
      } else {
        console.error('Failed to fetch analytics:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching analytics data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConferenceChange = (e) => {
    const conferenceName = e.target.value;
    setSelectedConferenceId(conferenceName);
    const conf = conferences.find(c => c.name === conferenceName);
    setSelectedConference(conf);
  };

  const isConferenceComplete = () => {
    if (!selectedConference || !selectedConference.endDate) return false;
    const endDate = new Date(selectedConference.endDate);
    const today = new Date();
    return endDate < today;
  };

  const renderBarChart = (label, value, total, color) => {
    const percentage = total > 0 ? (value / total) * 100 : 0;
    
    return (
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontWeight: '500', fontSize: '14px' }}>{label}</span>
          <span style={{ fontWeight: '600', fontSize: '14px' }}>
            {value} ({percentage.toFixed(1)}%)
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '30px',
          backgroundColor: '#e9ecef',
          borderRadius: '4px',
          overflow: 'hidden',
          position: 'relative'
        }}>
          <div style={{
            width: `${percentage}%`,
            height: '100%',
            backgroundColor: color,
            transition: 'width 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '600',
            fontSize: '13px'
          }}>
            {percentage > 10 && `${percentage.toFixed(0)}%`}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: '0 0 10px 0', fontSize: '28px', color: '#2c3e50' }}>Analytics Dashboard</h1>
        <p style={{ margin: 0, color: '#7f8c8d' }}>View registration statistics and payment status</p>
      </div>

      <div style={{ marginBottom: '30px' }}>
        <label style={{ display: 'block', marginBottom: '10px', fontWeight: '500', fontSize: '14px' }}>
          Select Conference:
        </label>
        <select
          value={selectedConferenceId}
          onChange={handleConferenceChange}
          style={{
            padding: '10px 15px',
            fontSize: '14px',
            borderRadius: '4px',
            border: '1px solid #ced4da',
            minWidth: '300px',
            backgroundColor: 'white'
          }}
        >
          <option value="">All Conferences</option>
          {conferences.map((conf) => (
            <option key={conf.id} value={conf.name}>
              {conf.name}
            </option>
          ))}
        </select>
      </div>

      {selectedConferenceId ? (
        <>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px', color: '#7f8c8d' }}>
              <div style={{ fontSize: '18px' }}>Loading analytics data...</div>
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                marginBottom: '40px'
              }}>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid #3498db'
                }}>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Total Registrations</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2c3e50' }}>{analyticsData.total}</div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid #27ae60'
                }}>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Paid</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>{analyticsData.paid}</div>
                  <div style={{ fontSize: '12px', color: '#2c3e50', marginTop: '8px', fontWeight: '600' }}>
                    Online: <strong>{analyticsData.onlinePayments}</strong> | Offline: <strong>{analyticsData.offlinePayments}</strong>
                  </div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid #f39c12'
                }}>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Pending</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f39c12' }}>{analyticsData.pending}</div>
                </div>
                <div style={{
                  backgroundColor: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  borderLeft: '4px solid #e74c3c'
                }}>
                  <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Failed</div>
                  <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e74c3c' }}>{analyticsData.failed}</div>
                </div>
                {isConferenceComplete() && (
                  <div style={{
                    backgroundColor: 'white',
                    padding: '20px',
                    borderRadius: '8px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    borderLeft: '4px solid #9b59b6'
                  }}>
                    <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Attended</div>
                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9b59b6' }}>{analyticsData.attended}</div>
                  </div>
                )}
              </div>

              {/* Bar Charts */}
              <div style={{
                backgroundColor: 'white',
                padding: '30px',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ marginTop: 0, marginBottom: '30px', fontSize: '20px', color: '#2c3e50' }}>
                  Registration Breakdown
                </h2>
                
                {renderBarChart('Paid Registrations', analyticsData.paid, analyticsData.total, '#27ae60')}
                {analyticsData.paid > 0 && (
                  <>
                    {renderBarChart('  - Online Payments', analyticsData.onlinePayments, analyticsData.total, '#3498db')}
                    {renderBarChart('  - Offline Payments', analyticsData.offlinePayments, analyticsData.total, '#16a085')}
                  </>
                )}
                {renderBarChart('Pending Registrations', analyticsData.pending, analyticsData.total, '#f39c12')}
                {renderBarChart('Failed Registrations', analyticsData.failed, analyticsData.total, '#e74c3c')}
                
                {isConferenceComplete() && (
                  <>
                    <h2 style={{ marginTop: '40px', marginBottom: '30px', fontSize: '20px', color: '#2c3e50' }}>
                      Attendance Statistics
                    </h2>
                    {renderBarChart('Attended', analyticsData.attended, analyticsData.total, '#9b59b6')}
                    {renderBarChart('Did Not Attend', analyticsData.total - analyticsData.attended, analyticsData.total, '#e74c3c')}
                  </>
                )}
              </div>

              {isConferenceComplete() && (
                <div style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#d4edda',
                  border: '1px solid #c3e6cb',
                  borderRadius: '4px',
                  color: '#155724'
                }}>
                  <strong>✓ Conference Complete:</strong> This conference has ended, showing full attendance statistics.
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '60px',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>📊</div>
          <h3 style={{ color: '#2c3e50', marginBottom: '10px' }}>Select a Conference</h3>
          <p style={{ color: '#7f8c8d' }}>Choose a conference from the dropdown above to view analytics</p>
        </div>
      )}
    </div>
  );
};

export default Analytics;
